import customHelper from 'v1/helpers/customFieldHelper';
import { displayCurrency, shortenInterval } from 'v1/helpers/currencyHelper';
import {
  displayDimension,
  displayHeight,
  displayMeasurement
} from 'v1/helpers/measurements/measurementsHelper';
import { displayDateRange } from 'v1/helpers/byType/dateHelper';
import { FeatureFlag } from '__types__';

import moment from 'moment-timezone';

import find from 'lodash/find';
import get from 'lodash/get';
import keys from 'lodash/keys';
import flatten from 'lodash/flatten';
import isEmpty from 'lodash/isEmpty';
import uniqBy from 'lodash/uniqBy';
import { has } from 'lodash';
import { CapabilityEnum } from 'dtos/constants/capability.enum';
import { unitFieldUtils } from '../../v4/shared/components/forms/input/unit-field-utils.js';
import { maybe } from '@passionware/monads';
import store from 'store/index.js';
import { publicInstance } from 'v5/i18n.public';
import { socialResolvers } from 'v5/core/contact-details/social/socialResolvers';
import { resolveCustomFieldValue } from 'v5/core/custom-fields/resolveCustomFieldValue';
import { orderedRates } from 'v5/core/entity-values/codecs/rate.codec';

/**
 * Utility function to safely get QR code field mapping from store
 * @returns {Object} Parsed QR code mapping or empty object if not found/invalid
 */
export const getQRCodeFieldMapping = () => {
  const qrCodeFieldMappingRaw =
    get(
      store.getState(),
      'auth.settings.settings.shortlist_qrcode_cs_mapping.value'
    ) ??
    get(
      store.getState(),
      'public_settings.settings.shortlist_qrcode_cs_mapping'
    );

  try {
    return qrCodeFieldMappingRaw ? JSON.parse(qrCodeFieldMappingRaw) : {};
  } catch (e) {
    console.warn('Failed to parse QR code field mapping:', e);
    return {};
  }
};

/**
 *
 * @param {Object} contact the contact data
 * @param {Integer} definitionId the custom definition id to match against
 * @param {number|null} [workspaceId] browsing lens (null = global only)
 * @returns {Object}
 */
const getCustomField = (contact, definitionId, workspaceId = null) => {
  return resolveCustomFieldValue(
    contact?.custom_fields,
    definitionId,
    workspaceId
  );
};

/**
 * @param {Object} contact - The contact data
 * @param {Array} custom_field_definitions - Array of custom field definitions
 * @param {number | null} [workspaceId] browsing lens (null = global only)
 * @returns {String} a formatted QR string
 */
const formatNikeShortlistQRString = (
  contact,
  custom_field_definitions = [],
  workspaceId = null
) => {
  // Get QR code field mapping from store
  const { mapping, options } = getQRCodeFieldMapping();
  const errors = [];

  const getCustomQRStringValue = (c, definitionId, fieldName) => {
    try {
      if (maybe.isAbsent(definitionId)) {
        throw new Error('QR code mapping definition ID is missing');
      }
      const field = find(custom_field_definitions, d => d.id === definitionId);
      const definition = field
        ? returnShortlistCustomDefinitionMap(field, workspaceId)
        : null;

      if (maybe.isAbsent(field)) {
        throw new Error(
          `QR code mapping definition not found for ID: ${definitionId}`
        );
      }

      if (field.data_type === 'MEASUREMENT_NEW') {
        // special case - qr code is generated differently, we can't reuse the existing pretty value function

        const displayUnit = options.display_unit;

        const value = definition.getValue(c);

        if (maybe.isAbsent(value?.unit) || maybe.isAbsent(value?.amount)) {
          return null;
        }

        // Convert from system unit to display unit via imperial
        const unitValue = unitFieldUtils.getUnitValueFromServerValue(
          value.amount,
          value.unit
        );
        const imperialValue =
          unitFieldUtils.getImperialValueFromUnitValue(unitValue);
        const convertedValue = unitFieldUtils.getUnitValueFromImperial(
          maybe.fromTruthy(imperialValue),
          displayUnit
        );

        return unitFieldUtils.getNumericalValue(convertedValue);
      }

      const value = definition
        ? definition.getPrettyValue(definition.getValue(c))
        : null;
      return value;
    } catch (error) {
      const errorMessage = `Error resolving ${fieldName}: ${error.message}`;
      errors.push({ message: errorMessage, field: fieldName });
      console.warn(errorMessage, error);
      return null;
    }
  };

  try {
    const sections = {
      first_name: c => c.first_name,
      last_name: c => c.last_name,
      groups: c => (c.groups || []).map(t => t.name).join(', '),
      gender: c => getCustomQRStringValue(c, mapping.gender, 'gender'),
      bust: c => getCustomQRStringValue(c, mapping.bust, 'bust'),
      waist: c => getCustomQRStringValue(c, mapping.waist, 'waist'),
      height: c => getCustomQRStringValue(c, mapping.height, 'height'),
      shoe: c => getCustomQRStringValue(c, mapping.shoe, 'shoe'),
      bra_size: c => getCustomQRStringValue(c, mapping.bra_size, 'bra_size'),
      guid: c => c.guid
    };

    const qrString = keys(sections)
      .map(s => `${sections[s](contact) || 'null'}`)
      .join('_');

    // Add errors field if there are any errors
    if (errors.length > 0) {
      return {
        qrString,
        __errors: errors
      };
    }

    return qrString;
  } catch (e) {
    console.error('Error formatting shortlist QR string', e);
    return {
      error: new Error(`Cannot map contact to QR string: ${e.message}`)
    };
  }
};

/**
 * Creates social field mapping with getPrettyValue and getUrl
 */
const createSocialFieldMapping = (platform, resolver) => ({
  label: platform.charAt(0).toUpperCase() + platform.slice(1),
  type: 'URL',
  getValue: contact => {
    const social =
      contact.socials && find(contact.socials, s => s.value_2 === platform);
    return social ? social.value_1 : null;
  },
  getPrettyValue: value => {
    const parsedResult = resolver.parseInput(value || '');
    return parsedResult?.handle || value;
  },
  getUrl: value => {
    const parsedResult = resolver.parseInput(value || '');
    return parsedResult?.fullUrl || null;
  }
});

const FIXED_FIELDS_MAPPING = {
  social_instagram: createSocialFieldMapping(
    'instagram',
    socialResolvers.instagram
  ),
  social_twitter: createSocialFieldMapping('twitter', socialResolvers.twitter),
  social_behance: createSocialFieldMapping('behance', socialResolvers.behance),
  social_youtube: createSocialFieldMapping('youtube', socialResolvers.youtube),
  social_vimeo: createSocialFieldMapping('vimeo', socialResolvers.vimeo),
  websites: {
    label: 'Website',
    type: 'URL',
    getValue: contact =>
      contact.websites && contact.websites.length
        ? contact.websites[0].value_1
        : null,
    getUrl: value => value
  },
  emails: {
    label: 'Email',
    type: 'EMAIL',
    getValue: contact =>
      contact.emails && contact.emails.length ? contact.emails[0].value_1 : null
  },
  phone_numbers: {
    label: 'Phone number',
    type: 'TEXT',
    getValue: contact =>
      contact.phone_numbers && contact.phone_numbers.length
        ? contact.phone_numbers[0].value_1
        : null
  },
  locations: {
    label: 'Location',
    type: 'TEXT',
    getValue: contact =>
      contact.locations && contact.locations.length
        ? contact.locations[0].name
        : null
  },
  rates: {
    label: 'Rate',
    type: 'CURRENCY',
    getValue: (contact, rates = []) => {
      const rate = contact && orderedRates(contact)[0];

      // FOR RATE_ID: First check if contact.rates object has an internal rate card object (which is returned from public shortlist) if not use the rates store for logged in users
      return rate && rate.rate_id
        ? get(rate, 'rate') || get(rates, ['data', rate.rate_id])
        : rate;
    },
    getPrettyValue: value =>
      value
        ? `${displayCurrency(value.amount, value.currency)}${
            value.interval ? ' / ' : ''
          }${shortenInterval(value.interval) || ''}`
        : ''
  },
  tags: {
    label: 'Tags',
    type: 'TEXT',
    getValue: contact => contact.tags,
    getPrettyValue: value => (value ? value.map(t => t.tag).join(', ') : '')
  },
  organisation: {
    label: 'Organization',
    type: 'RESOURCE',
    getValue: contact => contact.organisation_id || contact.organisation,
    getPrettyValue: contact => get(contact, 'organisation.full_name')
  },
  represented_by: {
    label: 'Agent',
    type: 'RESOURCE',
    getValue: contact => contact.represented_by_id || contact.represented_by,
    getPrettyValue: contact => get(contact, 'represented_by.full_name')
  },
  groups: {
    label: 'Groups',
    type: 'TEXT',
    getValue: contact => (!isEmpty(contact.groups) ? contact.groups : null),
    getPrettyValue: value => (value ? value.map(t => t.name).join(', ') : null)
  },
  qr_code: {
    label: 'QR Code',
    type: 'QR_CODE',
    getValue: contact => contact,
    getPrettyValue: (value, custom_field_definitions) =>
      // Static map has no browsing scope; generateShortlistsFieldMapper overrides
      // this with the real workspaceId. null = global CF lens.
      formatNikeShortlistQRString(value, custom_field_definitions, null),
    flag: FeatureFlag.NIKECUSTOM_QR_CODE,
    capabilities: CapabilityEnum.CONFIGURE_CUSTOM_NIKE_QR_CODE
  }
};

/**
 * @param {Object} definition
 * @param {number | null} [workspaceId] browsing lens (null = global only)
 */
const returnShortlistCustomDefinitionMap = (definition, workspaceId = null) => {
  if (!definition) return null;
  switch (definition.data_type) {
    case 'URL':
      return {
        label: definition.name,
        type: 'URL',
        getValue: contact => {
          const custom = getCustomField(contact, definition.id, workspaceId);
          return custom ? custom.string_value : null;
        },
        getUrl: value => value || ''
      };
    case 'DATE_RANGE':
      return {
        label: definition.name,
        type: 'TEXT',
        getValue: contact => {
          const custom = getCustomField(contact, definition.id, workspaceId);
          return custom ? custom.string_value : null;
        },
        getPrettyValue: value => {
          return displayDateRange(value);
        }
      };
    case 'SHORT_TEXT':
    case 'LONG_TEXT':
    case 'NUMBER':
    case 'HEIGHT':
    case 'PERCENTAGE':
    case 'CURRENCY':
    case 'DATE':
    case 'BOOLEAN':
    default:
      return {
        label: definition.name,
        type: 'TEXT',
        getValue: contact => {
          const maybeCustom = getCustomField(
            contact,
            definition.id,
            workspaceId
          );
          return customHelper.getValue(maybeCustom, definition.data_type);
        },
        getPrettyValue: value => {
          if (definition.data_type === 'CURRENCY')
            return displayCurrency(value?.amount, value?.currency);
          if (definition.data_type === 'DATE')
            return moment(value).format('Do MMM YYYY');
          if (definition.data_type === 'BOOLEAN')
            switch (value) {
              case true:
                return publicInstance.t('ds:TernaryToggle.yes');
              case false:
                return publicInstance.t('ds:TernaryToggle.no');
              default:
                return publicInstance.t('ds:TernaryToggle.emptyValue');
            }
          if (definition.data_type === 'PERCENTAGE') return value; // TODO: removed the '%' for now because existing customers use this for values with decimal places
          if (definition.data_type === 'HEIGHT') return displayHeight(value);

          return value;
        }
      };
    case 'DIMENSION_2D':
    case 'DIMENSION_3D':
      return {
        label: definition.name,
        type: 'TEXT',
        getValue: contact => {
          const custom = getCustomField(contact, definition.id, workspaceId);
          const rawValue = custom
            ? customHelper.getValue(custom, definition.data_type)
            : null;

          const value = displayDimension(rawValue);
          return value && value !== '' ? value : null;
        },
        getPrettyValue: value => {
          return value;
        }
      };
    case 'MEASUREMENT':
      return {
        label: definition.name,
        type: 'TEXT',
        getValue: contact => {
          const custom = getCustomField(contact, definition.id, workspaceId);
          const rawValue = custom
            ? customHelper.getValue(custom, definition.data_type)
            : null;

          return rawValue && rawValue.amount ? rawValue : null;
        },
        getPrettyValue: value => {
          return displayMeasurement(value);
        }
      };
    case 'MEASUREMENT_NEW':
      return {
        label: definition.name,
        type: 'TEXT',
        getValue: contact => {
          const custom = getCustomField(contact, definition.id, workspaceId);
          const rawValue = custom
            ? customHelper.getValue(custom, definition.data_type)
            : null;
          return rawValue;
        },
        getPrettyValue: value => {
          return value &&
            maybe.isPresent(value.amount) &&
            maybe.isPresent(value.unit)
            ? unitFieldUtils.formatDisplay(
                unitFieldUtils.getUnitValueFromServerValue(
                  value.amount,
                  value.unit
                ),
                value.unit
              )
            : null;
        }
      };
    case 'SINGLE_SELECT':
      return {
        label: definition.name,
        type: 'TEXT',
        getValue: contact => {
          const custom = getCustomField(contact, definition.id, workspaceId);
          return custom ? custom.option_id : null;
        },
        getPrettyValue: value => {
          if (!value) return '';
          const option = find(definition.options, o => o.id === value);
          return option ? option.value : '';
        }
      };
    case 'MULTI_SELECT':
      return {
        label: definition.name,
        type: 'TEXT',
        getValue: contact => {
          const custom = getCustomField(contact, definition.id, workspaceId);
          return custom ? custom.multi_select_ids : null;
        },
        getPrettyValue: value => {
          if (!value || !value.length) return '';
          const options = value
            .map(id => {
              const opt = find(definition.options, o => o.id === id);
              return opt ? opt.value : null;
            })
            .filter(name => name);
          return options.length ? options.join(', ') : '';
        }
      };
  }
};

/**
 *
 * @param {Array} resourceTypes
 * @returns {Array} unique array of allowed fields based on the passed in resource types
 */
export const getAllResourceTypeFields = (resourceTypes = []) => {
  const availableTypes = flatten(
    resourceTypes
      .filter(type => type && type.metastructure && type.metastructure.fields)
      .map(type => get(type, 'metastructure.fields'))
  );
  return uniqBy(
    availableTypes,
    field => field.custom_field_definition_id || field.name
  );
};

/**
 * @param {unknown[]} [fields]
 * @param {unknown} custom_field_definitions
 * @param {number | null} [workspaceId] browsing lens (null = global only)
 */
export const generateShortlistsFieldMapper = (
  fields = [],
  custom_field_definitions,
  workspaceId = null
) => {
  const mapping = fields.reduce((result, key) => {
    let field;
    if (typeof key === 'string') {
      if (!fields[key]?.active) return result;
      field = key;
    } else {
      if (has(key, 'active') && !key?.active) return result; // Todo: we pass through the active flag from from the resourcesFields which is needed to display the correct fields within ShortlistFieldManager
      field = key.name || `custom_${key.custom_field_definition_id}`;
    }
    if (!key.custom_field_definition_id) return result;

    const definition = find(
      custom_field_definitions,
      d => d.id === key.custom_field_definition_id
    );
    if (!definition) return result;

    const map = returnShortlistCustomDefinitionMap(definition, workspaceId);
    return { ...result, [field]: map };
  }, FIXED_FIELDS_MAPPING);

  return {
    ...mapping,
    qr_code: {
      ...mapping.qr_code,
      getPrettyValue: (value, custom_field_definitions) =>
        formatNikeShortlistQRString(
          value,
          custom_field_definitions,
          workspaceId
        )
    }
  };
};
